import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Tabs,
  Tab,
  Fade,
  Breadcrumbs,
  Link
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import { 
  Devices as DevicesIcon,
  Key as KeyIcon,
  Token as TokenIcon,
  Map as MapIcon,
  Language as LanguageIcon,
  Sync as SyncIcon,
  Download as DownloadIcon,
  AccountTree as AccountTreeIcon
} from '@mui/icons-material';

// Импорты компонентов табов
import { RegisteredDevicesTab } from './tabs/RegisteredDevicesTab';
import { RegistrationTokensTab } from './tabs/RegistrationTokensTab';
import { DeviceApiKeysTab } from './tabs/DeviceApiKeysTab';
import { DeviceMapTab } from './tabs/DeviceMapTab';
import { WebPlayersTab } from './tabs/WebPlayersTab';
import { PlaybackSyncSessionsTab } from './tabs/PlaybackSyncSessionsTab';
import { ClientDownloadsTab } from './tabs/ClientDownloadsTab';
import { AssignmentsOverviewTab } from './tabs/AssignmentsOverviewTab';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`device-tabpanel-${index}`}
      aria-labelledby={`device-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Fade in={true} timeout={300}>
          <Box sx={{ 
            pt: index === 7 ? 0 : 3, // Убираем верхний отступ для вкладки карты
            height: index === 7 ? '100%' : 'auto' // Полная высота для карты
          }}>
            {children}
          </Box>
        </Fade>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `device-tab-${index}`,
    'aria-controls': `device-tabpanel-${index}`,
  };
}

export const DeviceManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [currentTab, setCurrentTab] = useState(searchParams.get('device') ? 0 : 0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  return (
    <Container 
      maxWidth="xl"
      sx={{ 
        py: 3,
        overflow: 'visible' // Разрешаем элементам выходить за границы контейнера
      }}
    >
      {/* Header с хлебными крошками */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link component={RouterLink} underline="hover" color="inherit" to="/">
            {t('navigation.home')}
          </Link>
          <Typography color="text.primary">
            {t('devices.management.title')}
          </Typography>
        </Breadcrumbs>
        
        <Typography variant="h4" component="h1" gutterBottom>
          🖥️ {t('devices.management.title')}
        </Typography>
        
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {t('devices.management.description')}
        </Typography>
      </Box>

      {/* Основная панель с табами */}
      <Paper elevation={2} sx={{ overflow: 'hidden' }}>
        {/* Навигация по табам */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange} 
            aria-label="device management tabs"
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                minHeight: 64,
                fontWeight: 500,
              }
            }}
          >
            <Tab 
              icon={<DevicesIcon />} 
              label={t('devices.management.tabs.devices')}
              {...a11yProps(0)} 
            />
            <Tab 
              icon={<TokenIcon />} 
              label={t('devices.management.tabs.registrationTokens')}
              {...a11yProps(1)} 
            />
            <Tab 
              icon={<KeyIcon />} 
              label={t('devices.management.tabs.apiKeys')}
              {...a11yProps(2)} 
            />
            <Tab 
              icon={<LanguageIcon />} 
              label="Web Players"
              {...a11yProps(3)} 
            />
            <Tab
              icon={<SyncIcon />}
              label="Sync Sessions"
              {...a11yProps(4)}
            />
            <Tab
              icon={<DownloadIcon />}
              label="Clients"
              {...a11yProps(5)}
            />
            <Tab
              icon={<AccountTreeIcon />}
              label="Связи"
              {...a11yProps(6)}
            />
            <Tab 
              icon={<MapIcon />} 
              label={t('devices.management.tabs.map')}
              {...a11yProps(7)}
            />
          </Tabs>
        </Box>

        {/* Контент табов */}
        <CustomTabPanel value={currentTab} index={0}>
          <RegisteredDevicesTab />
        </CustomTabPanel>

        <CustomTabPanel value={currentTab} index={1}>
          <RegistrationTokensTab />
        </CustomTabPanel>

        <CustomTabPanel value={currentTab} index={2}>
          <DeviceApiKeysTab />
        </CustomTabPanel>

        <CustomTabPanel value={currentTab} index={3}>
          <WebPlayersTab />
        </CustomTabPanel>

        <CustomTabPanel value={currentTab} index={4}>
          <PlaybackSyncSessionsTab />
        </CustomTabPanel>

        <CustomTabPanel value={currentTab} index={5}>
          <ClientDownloadsTab />
        </CustomTabPanel>

        <CustomTabPanel value={currentTab} index={6}>
          <AssignmentsOverviewTab />
        </CustomTabPanel>

        <CustomTabPanel value={currentTab} index={7}>
          <Box sx={{ 
            width: '100%',
            position: 'relative',
            mx: { xs: -1, sm: -2, md: -3 }, // Компенсируем отступы контейнера
            overflow: 'hidden' // Предотвращаем переполнение
          }}>
            <DeviceMapTab />
          </Box>
        </CustomTabPanel>
      </Paper>
    </Container>
  );
}; 
